import { FileText, Image as ImageIcon } from 'lucide-react'

import { isPreviewable } from '../../../../../components/AgentChat/previewDocument'
import { ChatItemAttachment } from '../../../../../models/agentConversation'

const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp'])

function formatSize(bytes: number): string {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

interface Props {
  attachments: ChatItemAttachment[]
  align?: 'start' | 'end'
  /** When set, a chip the browser can render opens beside the chat instead of in a new tab. */
  onPreview?: (attachment: ChatItemAttachment) => void
}

export function ChatMessageAttachments({ attachments, align = 'end', onPreview }: Props) {
  if (attachments.length === 0) return null

  return (
    <div className={`flex flex-wrap gap-1.5 ${align === 'end' ? 'justify-end' : 'justify-start'}`}>
      {attachments.map((att) => {
        const ext = (att.fileExtension || '').toLowerCase().replace(/^\./, '')
        const Icon = IMAGE_EXTENSIONS.has(ext) ? ImageIcon : FileText
        const key = att.attachmentId || att.documentId
        const body = (
          <span className="bg-background/70 hover:bg-background/90 inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs font-normal transition-colors">
            <Icon className="size-3.5 shrink-0" />
            <span className="max-w-56 truncate">{att.fileName}</span>
            {att.fileSize > 0 && (
              <span className="text-muted-foreground text-[10px]">{formatSize(att.fileSize)}</span>
            )}
          </span>
        )
        // Anything the browser can't render inline stays a plain download link.
        if (onPreview && isPreviewable(att.fileName)) {
          return (
            <button key={key} type="button" className="cursor-pointer" onClick={() => onPreview(att)}>
              {body}
            </button>
          )
        }
        return att.fileUrl ? (
          <a key={key} href={att.fileUrl} target="_blank" rel="noopener noreferrer">
            {body}
          </a>
        ) : (
          <span key={key}>{body}</span>
        )
      })}
    </div>
  )
}
